import { GetServerSideProps, InferGetServerSidePropsType } from 'next';

import config from 'config';
import Breadcrumb from 'types/Breadcrumb';
import MappedSearchFiltersMeta from 'types/MappedSearchFiltersMeta';
import { Filter } from 'types/Filter';
import SearchItem from 'types/SearchItem';
import { SortId, sortValues } from 'consts/sort';
import withShopContext from 'core/hocs/withShopContext';
import withPageLayout from 'core/hocs/withPageLayout';
import {
  ServerSideProps as PageLayoutServerSideProps,
  serverRequests as pageLayoutServerRequests,
  GetPageLayoutResult,
} from '@layouts/PageLayout/PageLayout.utils';
import Search from '@pages/Search';
import { logPerformanceEnd, logPerformanceStart } from 'utils/performance';
import combineRequests, { RequestConfig } from 'utils/combineRequest';
import { startTransaction } from 'utils/startTransaction';
import graphQLRequest from 'utils/graphQLRequest';
import clearObject from 'utils/clearObject';
import handleError from 'utils/handleError';
import getMappedProductTypes from 'utils/getMappedProductTypes';
import getQueryFilters from 'utils/getQueryFilters';
import clearQuery from 'utils/clearQuery';
import keysOf from 'utils/keysOf';
import {
  GetProductTypesResult,
  ProductTypeResult,
  queryContent as productTypesQueryContent,
} from 'graphql/queries/GetProductTypes.query';
import {
  getQueryContent as getSearchItemsWithAttributesQueryContent,
  fragments as searchItemsWithAttributesFragments,
  SearchItemsWithAttributesResults,
  SearchItemsWithAttributesVariables,
} from 'graphql/queries/SearchItemsWithAttributes.query';
import {
  queryContent as searchFiltersQueryContent,
  SearchFiltersResults,
  SearchFiltersVariables,
} from 'graphql/queries/SearchFilters.query';
import { queryContent as pageQueryContent, GetPageResults, GetPageVariables } from 'graphql/queries/GetPageData.query';
import {
  GetPagesFilesResults,
  GetPagesFilesVariables,
  queryContent as pagesFilesQueryContent,
} from 'graphql/queries/GetPagesFiles.query';

const { routes } = config;

type Result = PageLayoutServerSideProps &
  Partial<{
    data?: SearchItem[];
    filters: Filter[];
    filtersProductTypeId?: number;
    meta: MappedSearchFiltersMeta;
    categoryName: string;
    productName?: string;
    searchName: string;
    manufacturer?: string | string[];
    family?: string | string[];
    totalItems: number;
    mainProductTypeId?: number;
    breadcrumbs: Breadcrumb[];
    productTypes: ProductTypeResult[];
  }>;

const serverRequests: RequestConfig[] = [
  ...pageLayoutServerRequests,
  { request: productTypesQueryContent },
  {
    request: getSearchItemsWithAttributesQueryContent(),
    fragments: searchItemsWithAttributesFragments,
    variables: {
      params: 'SearchParameters!',
    },
  },
  {
    request: searchFiltersQueryContent,
    variables: {
      params: 'SearchParameters!',
    },
  },
  {
    request: pageQueryContent,
    variables: { pages: '[String!]' },
  },
  {
    request: pagesFilesQueryContent,
    variables: { pages: '[String!]' },
  },
];

export const getServerSideProps: GetServerSideProps<Result> = withShopContext(async ({ query, res }, { client }) => {
  if (!keysOf(query).length) {
    return {
      redirect: {
        destination: routes.home.href,
        permanent: true,
      },
    };
  }

  const clearedQuery = clearQuery(query as Record<string, string>);
  const {
    name = '',
    page: queryPage = '',
    limit: queryLimit = '',
    sort_id: querySortId = SortId.Popularity,
    product: productName = '',
    product_type: categoryName,
    manufacturer = '',
    product_family: family = '',
  } = clearedQuery;

  const transaction = startTransaction({ op: 'server.requests', name: routes.search.href });

  const page = Number(queryPage) || 1;
  const limit = Number(queryLimit) || 20;
  const offset = (page - 1) * limit;
  const order = sortValues[querySortId as SortId];

  const combinedQuery = clearedQuery;
  const queryFilters = getQueryFilters(combinedQuery);

  const combinedDataSpan = transaction.startChild({ op: 'combined-data' });
  logPerformanceStart('search');
  const { data: combinedData, error } = await graphQLRequest<
    GetPageLayoutResult &
      GetProductTypesResult &
      SearchItemsWithAttributesResults &
      SearchFiltersResults &
      GetPageResults &
      GetPagesFilesResults,
    SearchItemsWithAttributesVariables & SearchFiltersVariables & GetPageVariables & GetPagesFilesVariables
  >(client, combineRequests(serverRequests), {
    variables: {
      params: { name, limit, offset, order, filters: queryFilters },
      pages: [routes.search.href],
    },
  });
  logPerformanceEnd('search');
  combinedDataSpan.finish();
  transaction.finish();

  if (error) {
    return handleError(res, 500);
  }

  const data = combinedData?.search.map(({ variant, attributes }) => ({ variant, attributes }));
  const filters = combinedData?.searchFilters.filters;
  const meta = combinedData?.searchFilters.meta;
  const totalItems = combinedData?.searchFilters.hits;
  const filtersProductTypeId = combinedData?.searchFilters.type?.id;
  const mappedProductTypes = getMappedProductTypes(combinedData?.productTypes);
  const mainProductTypeId = mappedProductTypes?.find(({ name: typeName }) => typeName === combinedQuery.product_type)
    ?.id;

  return {
    props: clearObject({
      shopFiles: combinedData?.shopFiles,
      pagesFiles: combinedData?.pagesFiles,
      productTypes: mappedProductTypes,
      mainMenu: combinedData?.mainMenu,
      data,
      holidays: combinedData?.holidays,
      categoryName,
      productName,
      searchName: name,
      manufacturer,
      family,
      filters,
      filtersProductTypeId,
      meta: clearObject({
        correctedQuery: meta?.corrected_query,
        exactMatches: meta?.exact_matches || 0,
        exactMatchesLowCountIsOk: !!meta?.exact_matches_low_count_is_ok,
      }),
      totalItems,
      mainProductTypeId,
    }),
  };
});

const SearchPage = ({
  data,
  filters,
  filtersProductTypeId,
  meta,
  categoryName,
  productName,
  productTypes,
  searchName,
  manufacturer,
  family,
  totalItems,
  mainProductTypeId,
}: InferGetServerSidePropsType<typeof getServerSideProps>): JSX.Element => (
  <Search
    categoryName={categoryName}
    productName={!Array.isArray(productName) ? productName : ''}
    manufacturer={!Array.isArray(manufacturer) ? manufacturer : ''}
    family={!Array.isArray(family) ? family : ''}
    data={data}
    filters={filters}
    filtersProductTypeId={filtersProductTypeId}
    meta={meta}
    searchName={searchName}
    totalItems={totalItems}
    mainProductTypeId={mainProductTypeId}
    productTypes={productTypes}
  />
);

export default withPageLayout(SearchPage, { showBreadcrumb: true, breadcrumbVariant: 'search' }, ['searchName']);
